import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency, yearOf } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { formatRSD, fmtDate } from '../lib/helpers';
import Icon from './Icon';
import SefSendDialog, { fnError, sefLabel } from './SefSendDialog';

// ============================================================
// eFakture (SEF): povezivanje (API ključ, demo/produkcija), poslate eFakture, fakture za slanje
// ============================================================
export default function EFakturePage() {
  const { activeAgencyId, year } = useAgency();
  const { tr } = useI18n();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ api_key: '', environment: 'demo' });
  const [hasKey, setHasKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState({});
  const [sendFor, setSendFor] = useState(null);
  const [rowBusy, setRowBusy] = useState('');
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const [setup, setSetup] = useState(() => new URLSearchParams(location.search).get('podesi') === '1');

  const load = useCallback(async () => {
    if (!activeAgencyId) return;
    const { data: s } = await supabase.from('sef_settings').select('*').eq('agency_id', activeAgencyId).maybeSingle();
    if (s) { setCfg({ api_key: s.api_key, environment: s.environment }); setHasKey(true); } else { setCfg({ api_key: '', environment: 'demo' }); setHasKey(false); }
    setLoaded(true);
    const { data: inv } = await supabase.from('invoices').select('*').eq('agency_id', activeAgencyId).order('issue_date', { ascending: false });
    const list = (inv || []).filter((i) => yearOf(i.issue_date) === year && (i.document_kind || 'faktura') === 'faktura');
    setDocs(list);
    const ids = [...new Set(list.map((i) => i.customer_id).filter(Boolean))];
    if (ids.length) {
      const { data: cs } = await supabase.from('customers').select('id, name, is_foreign, pib').in('id', ids);
      setCustomers(Object.fromEntries((cs || []).map((c) => [c.id, c])));
    }
  }, [activeAgencyId, year]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg({ ok: '', err: '' });
    const { error } = await supabase.from('sef_settings').upsert({ agency_id: activeAgencyId, api_key: cfg.api_key.trim(), environment: cfg.environment, updated_at: new Date().toISOString() }, { onConflict: 'agency_id' });
    setSaving(false);
    if (error) { setMsg({ ok: '', err: error.message }); return; }
    setHasKey(true);
    setMsg({ ok: tr('Sačuvano.', 'Saved.', 'Сохранено.'), err: '' });
  };

  const test = async () => {
    setTesting(true); setMsg({ ok: '', err: '' });
    const { data, error } = await supabase.functions.invoke('sef', { body: { action: 'test', agency_id: activeAgencyId } });
    setTesting(false);
    const e = await fnError(error, data);
    if (e || !data?.ok) { setMsg({ ok: '', err: e || `${data?.error || ''} ${data?.detail || ''}` }); return; }
    setMsg({ ok: data.env === 'prod' ? tr('Veza sa SEF-om radi (produkcija).', 'Connected to SEF (production).', 'Связь с SEF есть (прод).') : tr('Veza sa SEF-om radi (demo okruženje).', 'Connected to SEF (demo).', 'Связь с SEF есть (демо).'), err: '' });
  };

  const refresh = async (inv) => {
    setRowBusy(inv.id);
    const { data, error } = await supabase.functions.invoke('sef', { body: { action: 'status', invoice_id: inv.id } });
    setRowBusy('');
    const e = await fnError(error, data);
    if (e) { setMsg({ ok: '', err: e }); return; }
    setDocs((d) => d.map((x) => (x.id === inv.id ? { ...x, sef_status: data.status } : x)));
  };

  const sent = docs.filter((d) => d.sef_invoice_id);
  const canSend = (d) => !d.sef_invoice_id && (d.currency || 'RSD') === 'RSD' && d.type !== 'inostrana' && customers[d.customer_id] && !customers[d.customer_id].is_foreign && String(customers[d.customer_id].pib || '').replace(/\D/g, '').length === 9 && String(d.status || '').toLowerCase() !== 'stornirano';
  const toSend = docs.filter(canSend).slice(0, 30);

  // Firma još nije povezana sa SEF-om: uvodni ekran
  if (loaded && !hasKey && !setup) {
    return (
      <div style={s.introWrap}>
        <div style={s.intro} className="glow-card">
          <span style={s.introIcon}><Icon name="fileText" size={28} color="var(--teal-800)" /></span>
          <h1 style={s.introTitle}>{tr('eFakture za vašu firmu', 'e-Invoices for your company', 'Э-счета для вашей фирмы')}</h1>
          <p style={s.introText}>{tr('Šaljite fakture državnim institucijama i firmama direktno preko Sistema elektronskih faktura (SEF), iz FinArt Studija.', 'Send invoices to public institutions and companies directly through the national e-invoice system (SEF).', 'Отправляйте счета через государственную систему SEF.')}</p>
          <ul style={s.benefits}>
            {[
              tr('Slanje fakture u SEF jednim klikom', 'Send an invoice to SEF in one click', 'Отправка в один клик'),
              tr('Praćenje statusa: poslata, viđena, prihvaćena, odbijena', 'Status tracking: sent, seen, accepted, rejected', 'Отслеживание статуса'),
              tr('Jednostavno povezivanje — samo API ključ sa SEF portala', 'Simple setup — just an API key from the SEF portal', 'Простое подключение — только API-ключ'),
              tr('Ispravna kategorija za paušalce (SS, član 33) — bez ručnog podešavanja', 'Correct flat-rate category (SS, Art. 33) — no manual setup', 'Правильная категория для паушальцев'),
            ].map((b) => (
              <li key={b} style={s.benefit}><span style={s.benefitDot}><Icon name="check" size={12} color="var(--teal-800)" strokeWidth={2.4} /></span>{b}</li>
            ))}
          </ul>
          <button onClick={() => setSetup(true)} style={{ ...s.primary, width: '100%', justifyContent: 'center', minHeight: 38 }}>
            {tr('Podesi SEF integraciju', 'Set up SEF integration', 'Настроить SEF')}
          </button>
          <button onClick={() => navigate('/efakture/uputstvo')} style={s.linkBtn}>
            <Icon name="fileText" size={14} />{tr('Pogledajte uputstvo za podešavanje SEF-a', 'See the SEF setup guide', 'Инструкция по настройке')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.headRow}>
        <div>
          <div className="mono-label">{tr('Fakturisanje · SEF', 'Invoicing · SEF', 'Счета · SEF')}</div>
          <h1 style={s.heading}>{tr('eFakture', 'e-Invoices', 'Э-счета')}</h1>
        </div>
        <button onClick={() => navigate('/efakture/uputstvo')} style={s.outline}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="fileText" size={14} />{tr('Uputstvo', 'Guide', 'Инструкция')}</span>
        </button>
      </div>

      {/* Povezivanje */}
      <section style={s.card}>
        <div style={s.cardHead}>
          <span style={s.cardIcon}><Icon name="globe" size={15} color="var(--lavender-700)" /></span>
          <h2 style={s.cardTitle}>{tr('Povezivanje sa SEF-om', 'Connect to SEF', 'Подключение к SEF')}</h2>
          {hasKey && <span style={s.tag} className="mono">{cfg.environment === 'prod' ? tr('PRODUKCIJA', 'PRODUCTION', 'ПРОД') : 'DEMO'}</span>}
        </div>
        <ol style={s.steps}>
          <li>{tr('Prijavite se na', 'Log in to', 'Войдите на')} <a href={cfg.environment === 'prod' ? 'https://efaktura.mfin.gov.rs' : 'https://demoefaktura.mfin.gov.rs'} target="_blank" rel="noreferrer">{cfg.environment === 'prod' ? 'efaktura.mfin.gov.rs' : 'demoefaktura.mfin.gov.rs'}</a> {tr('(za demo je potreban poseban nalog na demo portalu).', '(the demo portal has its own account).', '(для демо — отдельный аккаунт).')}</li>
          <li>{tr('Podešavanja → Upravljanje API → generišite API ključ.', 'Settings → API management → generate an API key.', 'Настройки → API → создайте ключ.')}</li>
          <li>{tr('Nalepite ključ ovde, izaberite okruženje, sačuvajte i kliknite „Proveri vezu“.', 'Paste the key here, choose the environment, save and click “Test connection”.', 'Вставьте ключ, сохраните и проверьте связь.')}</li>
        </ol>
        <div style={s.formRow}>
          <label style={{ flex: '1 1 320px' }}>
            <span style={s.label}>{tr('SEF API ključ', 'SEF API key', 'API-ключ SEF')}</span>
            <div style={{ position: 'relative' }}>
              <input type={showKey ? 'text' : 'password'} value={cfg.api_key} onChange={(e) => setCfg((c) => ({ ...c, api_key: e.target.value }))} style={s.input} autoComplete="off" />
              <button type="button" onClick={() => setShowKey((v) => !v)} style={s.eye} aria-label={tr('Prikaži ključ', 'Show key', 'Показать')}><Icon name="eye" size={15} /></button>
            </div>
          </label>
          <div>
            <span style={s.label}>{tr('Okruženje', 'Environment', 'Среда')}</span>
            <div style={s.seg}>
              {[['demo', 'Demo'], ['prod', tr('Produkcija', 'Production', 'Продакшн')]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setCfg((c) => ({ ...c, environment: v }))} style={{ ...s.segBtn, ...(cfg.environment === v ? s.segOn : {}) }}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={s.btns}>
          <button onClick={save} disabled={saving || !cfg.api_key.trim()} style={{ ...s.primary, ...(saving || !cfg.api_key.trim() ? { opacity: 0.6 } : {}) }}><Icon name="check" size={14} />{saving ? tr('Čuvam…', 'Saving…', 'Сохранение…') : tr('Sačuvaj', 'Save', 'Сохранить')}</button>
          <button onClick={test} disabled={testing || !hasKey} style={{ ...s.outline, ...(testing || !hasKey ? { opacity: 0.6 } : {}) }}>{testing ? tr('Proveravam…', 'Testing…', 'Проверка…') : tr('Proveri vezu', 'Test connection', 'Проверить связь')}</button>
        </div>
        {msg.ok && <div style={s.ok}>{msg.ok}</div>}
        {msg.err && <div style={s.err}>{msg.err}</div>}
        <p style={s.note}>{tr('Preporuka: prvo probajte u Demo okruženju. Kad demo slanje prođe, prebacite na Produkciju sa ključem sa pravog portala.', 'Tip: test in Demo first, then switch to Production with the key from the live portal.', 'Сначала проверьте в демо.')}</p>
      </section>

      {/* Za slanje */}
      <section style={s.card}>
        <div style={s.cardHead}>
          <span style={s.cardIcon}><Icon name="logout" size={15} color="var(--lavender-700)" /></span>
          <h2 style={s.cardTitle}>{tr('Fakture koje mogu u SEF', 'Invoices ready for SEF', 'Счета для SEF')}</h2>
          <span style={s.count}>{toSend.length}</span>
        </div>
        <p style={s.note}>{tr('Domaće fakture u dinarima, klijentu sa PIB-om, koje još nisu poslate.', 'Domestic RSD invoices to a client with a tax ID, not yet sent.', 'Местные счета в RSD, ещё не отправленные.')}</p>
        {toSend.length === 0 ? <div style={s.empty}>{tr('Nema faktura za slanje.', 'Nothing to send.', 'Нечего отправлять.')}</div> : (
          <div style={s.list}>
            {toSend.map((d) => (
              <div key={d.id} style={s.row}>
                <button onClick={() => navigate(`/fakturisanje/${d.id}/pregled`)} style={s.rowMain}>
                  <span style={s.no}>{d.invoice_number}</span>
                  <span style={s.client}>{customers[d.customer_id]?.name || '—'}</span>
                </button>
                <span style={s.date}>{fmtDate(d.issue_date)}</span>
                <span style={s.amount}>{formatRSD(d.amount)}</span>
                <button onClick={() => setSendFor(d)} style={s.smallBtn}><Icon name="logout" size={13} />{tr('Pošalji', 'Send', 'Отправить')}</button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Poslate */}
      <section style={s.card}>
        <div style={s.cardHead}>
          <span style={s.cardIcon}><Icon name="checkCircle" size={15} color="var(--lavender-700)" /></span>
          <h2 style={s.cardTitle}>{tr('Poslate eFakture', 'Sent e-invoices', 'Отправленные')}</h2>
          <span style={s.count}>{sent.length}</span>
        </div>
        {sent.length === 0 ? <div style={s.empty}>{tr('Još nijedna faktura nije poslata u SEF.', 'No invoices sent to SEF yet.', 'Пока ничего не отправлено.')}</div> : (
          <div style={s.list}>
            {sent.map((d) => (
              <div key={d.id} style={s.row}>
                <button onClick={() => navigate(`/fakturisanje/${d.id}/pregled`)} style={s.rowMain}>
                  <span style={s.no}>{d.invoice_number}</span>
                  <span style={s.client}>{customers[d.customer_id]?.name || '—'}</span>
                </button>
                <span style={s.date}>{d.sef_sent_at ? fmtDate(String(d.sef_sent_at).slice(0, 10)) : '—'}{d.sef_env === 'demo' ? ' · demo' : ''}</span>
                <span style={s.amount}>{formatRSD(d.amount)}</span>
                <span style={s.status}><span style={s.dot} />{sefLabel(d.sef_status)}</span>
                <button onClick={() => refresh(d)} disabled={rowBusy === d.id} style={s.iconBtn} title={tr('Osveži status', 'Refresh status', 'Обновить')} aria-label={tr('Osveži status', 'Refresh status', 'Обновить')}><Icon name="exchange" size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={{ ...s.card, ...s.soon }}>
        <div style={s.cardHead}>
          <span style={s.cardIcon}><Icon name="download" size={15} color="var(--lavender-700)" /></span>
          <h2 style={s.cardTitle}>{tr('Ulazne eFakture', 'Incoming e-invoices', 'Входящие')}</h2>
          <span style={s.tag} className="mono">{tr('USKORO', 'SOON', 'СКОРО')}</span>
        </div>
        <p style={s.note}>{tr('Pregled i prihvatanje/odbijanje faktura koje primate kroz SEF — sledeći korak, posle probe slanja.', 'Viewing and accepting/rejecting received e-invoices — next step.', 'Входящие счета — следующий шаг.')}</p>
      </section>

      {sendFor && (
        <SefSendDialog
          invoice={sendFor}
          customerName={customers[sendFor.customer_id]?.name}
          onClose={() => setSendFor(null)}
          onSent={(fresh) => setDocs((d) => d.map((x) => (x.id === fresh.id ? fresh : x)))}
        />
      )}
    </div>
  );
}

const s = {
  introWrap: { display: 'flex', justifyContent: 'center', padding: '24px 0' },
  intro: { width: '100%', maxWidth: 520, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: '32px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 },
  introIcon: { width: 64, height: 64, borderRadius: '50%', background: 'var(--lavender-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  introTitle: { margin: '4px 0 0', fontSize: 20, fontWeight: 700 },
  introText: { margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-700)' },
  benefits: { listStyle: 'none', margin: '6px 0 6px', padding: 0, display: 'flex', flexDirection: 'column', gap: 10, alignSelf: 'stretch', textAlign: 'left' },
  benefit: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: 'var(--ink-900)', lineHeight: 1.45 },
  benefitDot: { width: 20, height: 20, borderRadius: '50%', background: 'var(--lavender-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  linkBtn: { display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', color: 'var(--lavender-700)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 4 },
  page: { display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 980, margin: '0 auto' },
  headRow: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 },
  heading: { fontSize: 'var(--fs-page, 22px)', fontWeight: 700, margin: '4px 0 0' },
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 },
  soon: { opacity: 0.85 },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10 },
  cardIcon: { width: 28, height: 28, borderRadius: 8, background: 'var(--lavender-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTitle: { fontSize: 14, fontWeight: 600, margin: 0, flex: 1 },
  tag: { fontSize: 10.5, letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 6, background: 'var(--lavender-100)', color: 'var(--lavender-700)' },
  count: { fontSize: 11, color: 'var(--ink-500)', background: 'var(--paper)', borderRadius: 6, padding: '1px 7px' },
  steps: { margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: 'var(--ink-700)' },
  formRow: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
  label: { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5 },
  input: { width: '100%', height: 36, border: '1px solid var(--line)', borderRadius: 8, padding: '0 38px 0 10px', fontSize: 14, fontFamily: 'var(--font-mono)' },
  eye: { position: 'absolute', right: 4, top: 3, width: 30, height: 30, border: 'none', background: 'transparent', color: 'var(--ink-500)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  seg: { display: 'inline-flex', gap: 2, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 9, padding: 3 },
  segBtn: { height: 28, border: 'none', background: 'transparent', borderRadius: 7, padding: '0 12px', fontSize: 13, fontWeight: 600, color: 'var(--ink-500)', cursor: 'pointer' },
  segOn: { background: 'var(--surface)', color: 'var(--ink-900)', boxShadow: '0 0 0 1px var(--line)' },
  btns: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  primary: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  outline: { minHeight: 34, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-900)', borderRadius: 8, padding: '0 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  ok: { fontSize: 13, background: 'var(--lavender-100)', color: 'var(--ink-900)', borderRadius: 8, padding: '8px 10px' },
  err: { fontSize: 12.5, background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-word' },
  note: { margin: 0, fontSize: 12.5, color: 'var(--ink-500)', lineHeight: 1.5 },
  empty: { fontSize: 13, color: 'var(--ink-500)', padding: '6px 0' },
  list: { display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--line)' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' },
  rowMain: { flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', border: 'none', background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' },
  no: { fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' },
  client: { fontSize: 12, color: 'var(--ink-500)', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  date: { fontSize: 12.5, color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', minWidth: 90 },
  amount: { fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 110, textAlign: 'right' },
  status: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ink-700)', minWidth: 110 },
  dot: { width: 7, height: 7, borderRadius: 2, background: 'var(--lavender-500)' },
  smallBtn: { display: 'flex', alignItems: 'center', gap: 5, minHeight: 30, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, padding: '0 10px', fontWeight: 600, fontSize: 12.5, color: 'var(--teal-800)', cursor: 'pointer' },
  iconBtn: { width: 30, height: 30, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-700)', cursor: 'pointer' },
};
